import propTypes from 'prop-types';

/**
 * Primitivas de formulario reutilizables.
 *
 * Los formularios antiguos repiten las mismas cadenas de clases de Tailwind en cada
 * input, lo que hace imposible tocar el estilo o añadir un campo sin copiar/pegar.
 * Aquí están centralizadas: un formulario nuevo se reduce a declarar sus campos.
 *
 * Todos los campos van dentro de un <FormSection>. Por defecto ocupan la fila
 * completa, igual que en el resto de formularios del office; `span={1}` los deja a
 * media fila para poder emparejar dos campos cortos.
 */

const LABEL_CLASS = 'block text-sm font-medium text-gray-700 dark:text-gray-400';

const INPUT_BASE = 'focus:ring-primary-600 focus:border-primary-600 shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 disabled:opacity-60';

const INPUT_CLASS = 'mt-1 block w-full ' + INPUT_BASE;

const SELECT_CLASS = 'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 disabled:opacity-60';

const FILE_CLASS = 'mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400';

const spanClass = (span) => (span === 2 ? 'col-span-1 sm:col-span-2' : 'col-span-1');

const Spinner = ({ className = 'h-5 w-5' }) => (
    <svg className={'animate-spin text-primary-600 ' + className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
Spinner.propTypes = { className: propTypes.string };

/** Etiqueta + contenido + texto de ayuda, con el ancho de columna correcto. */
const Field = ({ id, label, required, hint, span, children }) => (
    <div className={spanClass(span)}>
        {label && (
            <label htmlFor={id} className={LABEL_CLASS}>
                {label}{required ? ' *' : ''}
            </label>
        )}
        {children}
        {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
);
Field.propTypes = {
    id: propTypes.string,
    label: propTypes.node,
    required: propTypes.bool,
    hint: propTypes.node,
    span: propTypes.number,
    children: propTypes.node,
};

/** Cabecera del formulario: título + botón de volver. */
export const FormHeader = ({ emoji, title, highlight, onBack, backLabel = 'Volver atrás' }) => (
    <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 p-4 mb-3">
        <h2 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {emoji}&nbsp;&nbsp;{title} {highlight && <span className="underline decoration-primary-500">{highlight}</span>}
        </h2>
        {onBack && (
            <div className="w-full md:w-auto flex flex-col md:flex-row items-stretch md:items-center justify-end flex-shrink-0">
                <button type="button" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300" onClick={onBack}>
                    {backLabel}
                </button>
            </div>
        )}
    </div>
);
FormHeader.propTypes = {
    emoji: propTypes.string,
    title: propTypes.node,
    highlight: propTypes.node,
    onBack: propTypes.func,
    backLabel: propTypes.node,
};

/** Bloque de campos con título y rejilla de 2 columnas. */
export const FormSection = ({ title, description, children, hidden = false }) => (
    <fieldset className={'mb-8 ' + (hidden ? 'hidden' : '')}>
        {title && <legend className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{title}</legend>}
        {description && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{description}</p>}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {children}
        </div>
    </fieldset>
);
FormSection.propTypes = {
    title: propTypes.node,
    description: propTypes.node,
    children: propTypes.node,
    hidden: propTypes.bool,
};

export const TextField = ({ name, label, value, onChange, onBlur, type = 'text', placeholder, required = false, disabled = false, loading = false, span = 2, hint, inputRef, ...rest }) => (
    <Field id={name} label={label} required={required} hint={hint} span={span}>
        <div className="relative">
            <input
                ref={inputRef}
                id={name}
                name={name}
                type={type}
                value={value ?? ''}
                placeholder={placeholder}
                required={required}
                disabled={disabled || loading}
                autoComplete={name}
                className={INPUT_CLASS}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={onBlur ? (e) => onBlur(e.target.value, e) : undefined}
                {...rest}
            />
            {loading && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 mt-1">
                    <Spinner />
                </div>
            )}
        </div>
    </Field>
);
TextField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    value: propTypes.any,
    onChange: propTypes.func.isRequired,
    onBlur: propTypes.func,
    type: propTypes.string,
    placeholder: propTypes.string,
    required: propTypes.bool,
    disabled: propTypes.bool,
    loading: propTypes.bool,
    span: propTypes.number,
    hint: propTypes.node,
    inputRef: propTypes.object,
};

/** Igual que TextField pero con type="date". */
export const DateField = (props) => <TextField {...props} type="date" />;

/**
 * Campo de texto con el botón de buscar pegado a la derecha, en la misma fila.
 * Enter dispara la búsqueda sin enviar el formulario.
 */
export const SearchField = ({ name, label, value, onChange, onSearch, buttonLabel = 'Buscar', placeholder, loading = false, disabled = false, span = 2, hint }) => (
    <Field id={name} label={label} hint={hint} span={span}>
        <div className="mt-1 flex">
            <input
                id={name}
                name={name}
                type="text"
                value={value ?? ''}
                placeholder={placeholder}
                disabled={disabled || loading}
                autoComplete={name}
                className={'flex-1 min-w-0 ' + INPUT_BASE + ' rounded-r-none'}
                onChange={(e) => onChange(name, e.target.value)}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    onSearch();
                }}
            />
            <button
                type="button"
                onClick={onSearch}
                disabled={disabled || loading}
                className="flex items-center gap-2 px-6 rounded-r-md border border-l-0 border-transparent text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-70 disabled:cursor-not-allowed"
            >
                {loading && <Spinner className="h-4 w-4 text-white" />}
                {buttonLabel}
            </button>
        </div>
    </Field>
);
SearchField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    value: propTypes.any,
    onChange: propTypes.func.isRequired,
    onSearch: propTypes.func.isRequired,
    buttonLabel: propTypes.node,
    placeholder: propTypes.string,
    loading: propTypes.bool,
    disabled: propTypes.bool,
    span: propTypes.number,
    hint: propTypes.node,
};

/** Select. `options` admite strings o { value, label }. */
export const SelectField = ({ name, label, value, onChange, options = [], placeholder, required = false, disabled = false, span = 2, hint }) => (
    <Field id={name} label={label} required={required} hint={hint} span={span}>
        <select
            id={name}
            name={name}
            value={value ?? ''}
            required={required}
            disabled={disabled}
            autoComplete={name}
            className={SELECT_CLASS}
            onChange={(e) => onChange(name, e.target.value)}
        >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option) => {
                const item = typeof option === 'string' ? { value: option, label: option } : option;
                return <option key={item.value} value={item.value}>{item.label}</option>;
            })}
        </select>
    </Field>
);
SelectField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    value: propTypes.any,
    onChange: propTypes.func.isRequired,
    options: propTypes.array,
    placeholder: propTypes.string,
    required: propTypes.bool,
    disabled: propTypes.bool,
    span: propTypes.number,
    hint: propTypes.node,
};

/**
 * Input de fichero. Con `multiple` devuelve un array de File por
 * `onChange(name, files)`; sin él, un File suelto (o null).
 */
export const FileField = ({ name, label, file, onChange, accept, multiple = false, required = false, disabled = false, span = 2, hint }) => {
    const seleccionados = multiple ? (file || []) : (file ? [file] : []);
    return (
        <Field id={name} label={label} required={required} hint={hint} span={span}>
            <input
                id={name}
                name={name}
                type="file"
                accept={accept}
                multiple={multiple}
                required={required && seleccionados.length === 0}
                disabled={disabled}
                className={FILE_CLASS}
                onChange={(e) => onChange(name, multiple ? Array.from(e.target.files) : (e.target.files[0] || null))}
            />
            {seleccionados.map((f, i) => (
                <span key={f.name + i} className="mt-1 text-sm text-emerald-600 dark:text-emerald-400 flex items-center">
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    {f.name}
                </span>
            ))}
        </Field>
    );
};
FileField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    file: propTypes.oneOfType([propTypes.object, propTypes.array]),
    onChange: propTypes.func.isRequired,
    accept: propTypes.string,
    multiple: propTypes.bool,
    required: propTypes.bool,
    disabled: propTypes.bool,
    span: propTypes.number,
    hint: propTypes.node,
};

/**
 * Lista de ficheros que se va acumulando: cada selección se AÑADE a lo que ya
 * hay, y cada fichero se puede quitar por separado. Para tandas donde el orden
 * importa y se suben en varias veces, como las páginas del libro de familia.
 */
export const FileListField = ({ name, label, files = [], onChange, accept, disabled = false, span = 2, hint, vacio }) => {
    const anadir = (e) => {
        const nuevos = Array.from(e.target.files);
        if (nuevos.length) onChange(name, [...files, ...nuevos]);
        // Permite volver a elegir el mismo fichero después de quitarlo.
        e.target.value = '';
    };

    return (
        <Field id={name} label={label} hint={hint} span={span}>
            <input
                id={name}
                name={name}
                type="file"
                accept={accept}
                multiple
                disabled={disabled}
                className={FILE_CLASS}
                onChange={anadir}
            />
            {files.length === 0 && vacio && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{vacio}</p>
            )}
            {files.length > 0 && (
                <ul className="mt-2 divide-y divide-gray-200 dark:divide-gray-600 rounded-md border border-gray-200 dark:border-gray-600">
                    {files.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                            <span className="flex items-center gap-2 min-w-0">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 text-xs flex items-center justify-center text-gray-600 dark:text-gray-300">{i + 1}</span>
                                <span className="truncate text-gray-700 dark:text-gray-300">{f.name}</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => onChange(name, files.filter((_, j) => j !== i))}
                                disabled={disabled}
                                className="flex-shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                            >
                                &times;
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Field>
    );
};
FileListField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    files: propTypes.array,
    onChange: propTypes.func.isRequired,
    accept: propTypes.string,
    disabled: propTypes.bool,
    span: propTypes.number,
    hint: propTypes.node,
    vacio: propTypes.node,
};

/** Casilla de verificación con etiqueta y descripción opcional. */
export const CheckboxField = ({ name, label, checked = false, onChange, description, disabled = false, span = 2 }) => (
    <div className={spanClass(span)}>
        <div className="flex items-start">
            <div className="flex items-center h-5">
                <input
                    id={name}
                    name={name}
                    type="checkbox"
                    checked={!!checked}
                    disabled={disabled}
                    className="focus:ring-primary-600 h-4 w-4 text-primary-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    onChange={(e) => onChange(name, e.target.checked)}
                />
            </div>
            <div className="ml-3 text-sm">
                <label htmlFor={name} className="font-medium text-gray-700 dark:text-gray-400">{label}</label>
                {description && <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">{description}</p>}
            </div>
        </div>
    </div>
);
CheckboxField.propTypes = {
    name: propTypes.string.isRequired,
    label: propTypes.node,
    checked: propTypes.bool,
    onChange: propTypes.func.isRequired,
    description: propTypes.node,
    disabled: propTypes.bool,
    span: propTypes.number,
};

/** Botón de envío que se convierte en spinner mientras `loading`. */
export const SubmitButton = ({ loading = false, label, loadingLabel }) => (
    <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-70 disabled:cursor-not-allowed"
    >
        {loading && <Spinner className="h-4 w-4 mr-3 text-white" />}
        {loading ? loadingLabel : label}
    </button>
);
SubmitButton.propTypes = {
    loading: propTypes.bool,
    label: propTypes.node,
    loadingLabel: propTypes.node,
};

